import { useState } from 'react';
import { Link } from 'react-router-dom';
import { languageNativeName } from '../../shared/languages';
import { resolveMenuName, useMenuNames } from '../lib/store';
import { relativeTime } from '../lib/relativeTime';
import { resolvePageSrc } from '../lib/resolvePageSrc';
import { SavedMenuNote } from './SavedMenuNote';
import type { MenuListEntry } from '../lib/savedLists';

interface MenuListItemProps {
  menu: MenuListEntry;
  // When false, omit the SavedMenuNote — useful on the Home recent-list to
  // keep cards compact. Defaults to true (Me page shows the full card).
  showNote?: boolean;
}

const THUMB_SIZE = 56;

export function MenuListItem({ menu, showNote = true }: MenuListItemProps) {
  const namesMap = useMenuNames((s) => s.names);
  const displayName = resolveMenuName(menu.menuId, menu.restaurantName, namesMap) ?? 'Menu';

  // Prefer the explicit local thumbnailRef. Fall back to the deterministic
  // proxy URL so server-pinned menus that haven't been opened on this device
  // still get a thumbnail when their pages exist server-side. onError swaps
  // to the camera icon for menus that genuinely have no page bytes.
  const initialSrc = menu.thumbnailRef
    ? resolvePageSrc(menu.menuId, menu.thumbnailRef, 0)
    : `/api/menu/${menu.menuId}/page/0`;
  const [thumbBroken, setThumbBroken] = useState(false);

  return (
    <li
      style={{
        padding: 12,
        borderRadius: 12,
        background: 'var(--color-surface)',
      }}
    >
      <Link
        to={`/menu/${menu.menuId}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          color: 'var(--color-primary)',
          textDecoration: 'none',
        }}
      >
        <div
          aria-hidden
          style={{
            width: THUMB_SIZE,
            height: THUMB_SIZE,
            flexShrink: 0,
            borderRadius: 8,
            overflow: 'hidden',
            background: 'var(--color-bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--color-text-muted)',
          }}
        >
          {thumbBroken ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
          ) : (
            <img
              src={initialSrc}
              alt=""
              loading="lazy"
              decoding="async"
              onError={() => setThumbBroken(true)}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 16,
              fontWeight: 500,
              marginBottom: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {displayName}
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
            {languageNativeName(menu.sourceLanguage)} → {languageNativeName(menu.targetLanguage)}
            {' · '}
            {relativeTime(menu.savedAt)}
          </div>
        </div>
      </Link>
      {showNote && <SavedMenuNote menuId={menu.menuId} />}
    </li>
  );
}
